<script lang='ts'>
export default {
  name: 'PasswordUpdateModal'
};
</script>

<script setup lang='ts'>
import { onMounted, onUnmounted, ref, computed } from 'vue';
import z, { SafeParseError } from 'zod';
import { permissionService } from '@/services/api';
import { UserPermissionDetailsWithRoles } from '@/types/UserPermissions';
import AppModal from '@/components/v2/AppModal.vue';

const formSchema = z.object({
  // password contains at least 14 characters, 1 lower, 1 upper, 1 special character, cannot contain leading or trailing spaces
  password: z.string().regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>])(?=.{14,})\S+$/).default('')
});

type FormSchemeType = z.infer<typeof formSchema>;

type ValidFormFields = {
  [K in keyof FormSchemeType]: boolean;
};

const defaultValidFormFields: ValidFormFields = {
  password: true
};

const props = defineProps<{
  userAccount: UserPermissionDetailsWithRoles;
  show: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'save', error: boolean): void;
}>();

// ----------------
// refs and consts
// ----------------
const form = ref<FormSchemeType>({ password: '' }); // zero values for all form fields
const validFormFields = ref<ValidFormFields>(structuredClone(defaultValidFormFields));

// ---------
// computed
// ---------

// -----------
// lifecycles
// -----------
onMounted(async () => {
  addEventListener('keyup', handleCloseWithEscapeKey);
});

onUnmounted(() => {
  removeEventListener('keyup', handleCloseWithEscapeKey);
});

// ---------
// handlers
// ---------
function handleCloseWithEscapeKey(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    handleClose();
  }
}

function handleClose(): void {
  emit('close');
}

async function handleSave(): Promise<void> {
  try {
    // validate the form
    if (!validateForm()) {
      console.log('invalid form');
      return;
    }
    
    // save the form
    await permissionService.updateUserPassword(props.userAccount.userSubId, form.value.password);
    emit('save', false);
  } catch (e) {
    console.error('policy edit save error', e);
    emit('save', true);
  }
}

const canSaveForm = computed(() => {
  return form.value.password !== '' && validateForm();
});

// ------------------
// private functions
// ------------------

function validateForm(): boolean {
  const valid = formSchema.safeParse(form.value);
  validFormFields.value = structuredClone(defaultValidFormFields);
  
  if (!valid.success) {
    // set valid form fields to false
    for (const error of (valid as SafeParseError<FormSchemeType>).error.issues) {
      console.log('error', error);
      validFormFields.value[error.path[0].toString() as keyof ValidFormFields] = false;
    }
  }
  
  return valid.success;
}
</script>

<template>
  <AppModal
    :show="show"
    title="Update Password"
    :handle-cancel="handleClose"
    :handle-action="handleSave"
    :action-disabled="!canSaveForm"
    action-button-label="Update Password"
    cancel-button-label="Cancel">
    <template #content>
      <div class='tw:flex tw:flex-col tw:gap-1'>
        <p class='tw:italic'>Set new password for {{ userAccount.userEmail }}</p>
        
        <div class='tw:flex'>
          <div class='tw:w-full'>
            <label class='tw:label'>
              <span class='tw:label-text tw:text-xs tw:text-gray-500'>New Password</span>
            </label>
            <input
              type='text'
              inputmode='numeric'
              placeholder=''
              data-testid='password-input'
              v-model='form.password'
              class='tw:input tw:input-md tw:border tw:border-solid tw:w-full'
              :class="!validFormFields.password && 'tw:border-red-500'"
            />
            <label class='tw:label'>
              <span v-show='!validFormFields.password'
                    class='tw:label-text-alt tw:text-xs tw:text-red-500'
              >
                Password must be minimum 14 characters long, with at least 1 upper letter, 1 lowercase letter, and a 1 Special Character.  Password must not contain a leading or trailing space.
              </span>
            </label>
          </div>
        </div>
      </div>
    </template>
  </AppModal>
</template>
